"use client";

import { useState } from "react";
import { useForm, type FieldValues } from "react-hook-form";
import { toast } from "sonner";

export function useDynamicForm<T extends FieldValues>(
    formId: string,
    fileFieldName?: keyof T
) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<T>({ mode: "onBlur" });

    const handleFileSelect = (file: File | null) => setSelectedFile(file);

    const onSubmit = async (data: T) => {
        try {
            const fd = new FormData();
            Object.entries(data).forEach(([key, value]) => {
                if (key !== fileFieldName && value !== undefined) {
                    fd.append(key, String(value));
                }
            });
            if (fileFieldName && selectedFile) {
                fd.append(fileFieldName as string, selectedFile);
            }

            const res = await fetch(`/api/forms/${formId}`, { method: "POST", body: fd });
            const result = await res.json();

            if (!res.ok) throw new Error(result.error || "Something went wrong.");

            toast.success("Submitted successfully — we'll be in touch soon.");
            reset();
            setSelectedFile(null);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Failed to send. Try again.");
        }
    };

    const onError = () => toast.error("Please check the highlighted fields.");

    return {
        register,
        handleSubmit,
        control,
        errors,
        isSubmitting,
        selectedFile,
        handleFileSelect,
        onSubmit,
        onError,
    };
}